import {
  createContext,
  useContext,
  createSignal,
  onMount,
  createEffect,
  batch,
  type ParentComponent,
} from 'solid-js';
import type { VimState, VimMode, VimCommand } from './types';

// Vim context interface
interface VimContextValue {
  state: () => VimState;
  executeCommand: (command: VimCommand, count?: number) => void;
  setMode: (mode: VimMode) => void;
  appendToBuffer: (char: string) => void;
  clearBuffer: () => void;
  toggleHelp: () => void;
  performSearch: (query: string) => void;
  setEnabled: (enabled: boolean) => void;
}

// Create context
const VimContext = createContext<VimContextValue | undefined>(undefined);

// Helper to find focusable elements
const getFocusableElements = (): HTMLElement[] => {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  return Array.from(document.querySelectorAll(selector)).filter(
    (el): el is HTMLElement => el instanceof HTMLElement && el.offsetParent !== null
  );
};

// Helper to get element center position
const getElementCenter = (el: HTMLElement): { x: number; y: number } => {
  const rect = el.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
};

// Helper to find closest element in a direction
const findClosestElement = (
  elements: HTMLElement[],
  current: HTMLElement,
  direction: 'up' | 'down' | 'left' | 'right'
): HTMLElement | null => {
  const currentCenter = getElementCenter(current);
  let closest: HTMLElement | null = null;
  let closestDistance = Infinity;

  elements.forEach(el => {
    if (el === current) {
      return;
    }

    const center = getElementCenter(el);
    let valid = false;
    let distance = Infinity;

    switch (direction) {
      case 'up':
        valid = center.y < currentCenter.y;
        distance = currentCenter.y - center.y + Math.abs(center.x - currentCenter.x) * 0.5;
        break;
      case 'down':
        valid = center.y > currentCenter.y;
        distance = center.y - currentCenter.y + Math.abs(center.x - currentCenter.x) * 0.5;
        break;
      case 'left':
        valid = center.x < currentCenter.x;
        distance = currentCenter.x - center.x + Math.abs(center.y - currentCenter.y) * 0.5;
        break;
      case 'right':
        valid = center.x > currentCenter.x;
        distance = center.x - currentCenter.x + Math.abs(center.y - currentCenter.y) * 0.5;
        break;
    }

    if (valid && distance < closestDistance) {
      closestDistance = distance;
      closest = el;
    }
  });

  return closest;
};

// Announce to screen readers
const announce = (message: string) => {
  const liveRegion = document.getElementById('aria-live-region');
  if (liveRegion) {
    liveRegion.textContent = message;
  }
};

// Provider component
export const VimProvider: ParentComponent = props => {
  // State
  const [enabled, setEnabled] = createSignal(false);
  const [mode, setMode] = createSignal<VimMode>('normal');
  const [commandBuffer, setCommandBuffer] = createSignal('');
  const [lastCommand, setLastCommand] = createSignal('');
  const [currentElement, setCurrentElement] = createSignal<HTMLElement | null>(null);
  const [yankBuffer, setYankBuffer] = createSignal('');
  const [searchBuffer, setSearchBuffer] = createSignal('');
  const [searchDirection, setSearchDirection] = createSignal<'forward' | 'backward'>('forward');
  const [searchMatches, setSearchMatches] = createSignal<HTMLElement[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = createSignal(0);
  const [showHelp, setShowHelp] = createSignal(false);

  // Delete history for undo functionality
  const [deletedElements, setDeletedElements] = createSignal<
    { element: HTMLElement; parent: HTMLElement; nextSibling: Node | null }[]
  >([]);

  // Get current element on mount (only highlight if vim is enabled)
  onMount(() => {
    const focusable = getFocusableElements();
    if (focusable.length > 0) {
      setCurrentElement(focusable[0]);
      if (enabled()) {
        highlightElement(focusable[0]);
      }
    }
  });

  // Add/remove highlight when vim mode is toggled
  createEffect(() => {
    if (enabled()) {
      const current = currentElement();
      if (current) {
        highlightElement(current);
      }
    } else {
      // Remove all highlights when vim is disabled
      document.querySelectorAll('.vim-highlight').forEach(elem => {
        elem.classList.remove('vim-highlight');
      });
    }
  });

  // Highlight current element
  const highlightElement = (el: HTMLElement | null) => {
    document.querySelectorAll('.vim-highlight').forEach(elem => {
      elem.classList.remove('vim-highlight');
    });
    if (el) {
      el.classList.add('vim-highlight');
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Execute vim command
  const executeCommand = (command: VimCommand, _count: number = 1) => {
    const elements = getFocusableElements();
    const current = currentElement();

    batch(() => {
      switch (command) {
        case 'moveUp':
          if (current) {
            const closest = findClosestElement(elements, current, 'up');
            if (closest) {
              setCurrentElement(closest);
              highlightElement(closest);
            }
          }
          break;

        case 'moveDown':
          if (!current && elements.length > 0) {
            setCurrentElement(elements[0]);
            highlightElement(elements[0]);
          } else if (current) {
            const closest = findClosestElement(elements, current, 'down');
            if (closest) {
              setCurrentElement(closest);
              highlightElement(closest);
            }
          }
          break;

        case 'moveLeft':
          if (current) {
            const closest = findClosestElement(elements, current, 'left');
            if (closest) {
              setCurrentElement(closest);
              highlightElement(closest);
            }
          }
          break;

        case 'moveRight':
          if (current) {
            const closest = findClosestElement(elements, current, 'right');
            if (closest) {
              setCurrentElement(closest);
              highlightElement(closest);
            }
          }
          break;

        case 'moveWordForward':
          if (current) {
            const idx = elements.indexOf(current);
            if (idx < elements.length - 1) {
              setCurrentElement(elements[idx + 1]);
              highlightElement(elements[idx + 1]);
            }
          }
          break;

        case 'moveWordBackward':
          if (current) {
            const idx = elements.indexOf(current);
            if (idx > 0) {
              setCurrentElement(elements[idx - 1]);
              highlightElement(elements[idx - 1]);
            }
          }
          break;

        case 'moveLineStart':
          if (elements.length > 0) {
            const first = elements[0];
            setCurrentElement(first);
            highlightElement(first);
          }
          break;

        case 'moveLineEnd':
          if (elements.length > 0) {
            const last = elements[elements.length - 1];
            setCurrentElement(last);
            highlightElement(last);
          }
          break;

        case 'moveDocumentStart':
          window.scrollTo({ top: 0, behavior: 'smooth' });
          if (elements.length > 0) {
            setCurrentElement(elements[0]);
            highlightElement(elements[0]);
          }
          break;

        case 'moveDocumentEnd':
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
          if (elements.length > 0) {
            const last = elements[elements.length - 1];
            setCurrentElement(last);
            highlightElement(last);
          }
          break;

        case 'scrollHalfPageDown':
          window.scrollBy({ top: window.innerHeight / 2, behavior: 'smooth' });
          break;

        case 'scrollHalfPageUp':
          window.scrollBy({ top: -window.innerHeight / 2, behavior: 'smooth' });
          break;

        case 'scrollPageDown':
          window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
          break;

        case 'scrollPageUp':
          window.scrollBy({ top: -window.innerHeight, behavior: 'smooth' });
          break;

        case 'click':
          if (current) {
            current.click();
            current.focus();
            // If it's a link, follow it
            if (current.tagName === 'A') {
              const href = current.getAttribute('href');
              if (href && !href.startsWith('#')) {
                window.location.href = href;
              }
            }
          }
          break;

        case 'enterInsert':
          if (current) {
            setMode('insert');
            current.focus();
            announce('Insert mode');
          }
          break;

        case 'enterNormal':
          setMode('normal');
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          announce('Normal mode');
          break;

        case 'enterCommand':
          setMode('command');
          announce('Command mode');
          break;

        case 'searchForward':
          setSearchDirection('forward');
          setMode('command');
          setCommandBuffer('/');
          announce('Search forward');
          break;

        case 'searchBackward':
          setSearchDirection('backward');
          setMode('command');
          setCommandBuffer('?');
          announce('Search backward');
          break;

        case 'nextSearchResult':
          {
            const matches = searchMatches();
            if (matches.length > 0) {
              const newIndex = (currentMatchIndex() + 1) % matches.length;
              setCurrentMatchIndex(newIndex);
              const nextMatch = matches[newIndex];
              setCurrentElement(nextMatch);
              highlightElement(nextMatch);
              announce(`Match ${newIndex + 1} of ${matches.length}`);
            }
          }
          break;

        case 'prevSearchResult':
          {
            const matches = searchMatches();
            if (matches.length > 0) {
              const newIndex = (currentMatchIndex() - 1 + matches.length) % matches.length;
              setCurrentMatchIndex(newIndex);
              const prevMatch = matches[newIndex];
              setCurrentElement(prevMatch);
              highlightElement(prevMatch);
              announce(`Match ${newIndex + 1} of ${matches.length}`);
            }
          }
          break;

        case 'toggleHelp':
          setShowHelp(prev => !prev);
          break;

        case 'yank':
          if (current) {
            const text = current.textContent || '';
            setYankBuffer(text);
            announce(`Yanked: ${text.slice(0, 50)}`);
          }
          break;

        case 'yankLine':
          if (current) {
            const text = current.textContent || '';
            setYankBuffer(text + '\n');
            announce(`Yanked line: ${text.slice(0, 50)}`);
          }
          break;

        case 'delete':
          if (current) {
            const text = current.textContent || '';
            setYankBuffer(text);
            // Store for undo
            const parent = current.parentElement;
            if (parent) {
              setDeletedElements(prev => [
                ...prev,
                {
                  element: current,
                  parent,
                  nextSibling: current.nextSibling,
                },
              ]);
            }
            current.remove();
            announce(`Deleted: ${text.slice(0, 50)}`);
            // Move to next element
            const remaining = getFocusableElements();
            if (remaining.length > 0) {
              setCurrentElement(remaining[0]);
              highlightElement(remaining[0]);
            }
          }
          break;

        case 'deleteLine':
          if (current) {
            const text = current.textContent || '';
            setYankBuffer(text + '\n');
            // Store for undo
            const parent = current.parentElement;
            if (parent) {
              setDeletedElements(prev => [
                ...prev,
                {
                  element: current,
                  parent,
                  nextSibling: current.nextSibling,
                },
              ]);
            }
            current.remove();
            announce(`Deleted line: ${text.slice(0, 50)}`);
            // Move to next element
            const remaining = getFocusableElements();
            if (remaining.length > 0) {
              setCurrentElement(remaining[0]);
              highlightElement(remaining[0]);
            }
          }
          break;

        case 'paste':
          {
            const yanked = yankBuffer();
            if (yanked && current) {
              // Try to paste into input elements
              if (current.tagName === 'INPUT' || current.tagName === 'TEXTAREA') {
                const input = current as HTMLInputElement | HTMLTextAreaElement;
                const start = input.selectionStart || 0;
                const end = input.selectionEnd || 0;
                const value = input.value;
                input.value = value.slice(0, start) + yanked + value.slice(end);
                input.selectionStart = input.selectionEnd = start + yanked.length;
                announce(`Pasted: ${yanked.slice(0, 50)}`);
              }
            }
          }
          break;

        case 'undo':
          {
            const history = deletedElements();
            if (history.length > 0) {
              const lastDeleted = history[history.length - 1];
              if (lastDeleted.nextSibling) {
                lastDeleted.parent.insertBefore(lastDeleted.element, lastDeleted.nextSibling);
              } else {
                lastDeleted.parent.appendChild(lastDeleted.element);
              }
              setDeletedElements(history.slice(0, -1));
              setCurrentElement(lastDeleted.element);
              highlightElement(lastDeleted.element);
              announce('Undo: restored element');
            }
          }
          break;

        case 'redo':
          // Redo is not fully implemented due to complexity
          // Would need a separate redo stack
          announce('Redo not implemented');
          break;

        case 'none':
        default:
          break;
      }

      setLastCommand(command);
      setCommandBuffer('');
    });
  };

  // Append to command buffer
  const appendToBuffer = (char: string) => {
    setCommandBuffer(prev => prev + char);
  };

  // Clear command buffer
  const clearBuffer = () => {
    setCommandBuffer('');
  };

  // Toggle help
  const toggleHelp = () => {
    setShowHelp(prev => !prev);
  };

  // Search function
  const performSearch = (query: string) => {
    if (!query || query === '/' || query === '?') {
      setSearchMatches([]);
      return;
    }

    const searchTerm = query.replace(/^[/?]/, '').toLowerCase();
    setSearchBuffer(searchTerm);

    const allElements = Array.from(document.querySelectorAll('*'));
    const matches: HTMLElement[] = [];

    allElements.forEach(el => {
      if (el instanceof HTMLElement && el.textContent) {
        const text = el.textContent.toLowerCase();
        if (text.includes(searchTerm) && isFocusable(el)) {
          matches.push(el);
        }
      }
    });

    setSearchMatches(matches);
    setCurrentMatchIndex(0);

    if (matches.length > 0) {
      setCurrentElement(matches[0]);
      highlightElement(matches[0]);
      announce(`Found ${matches.length} matches`);
    } else {
      announce('No matches found');
    }
  };

  // Helper to check if element is focusable
  const isFocusable = (el: HTMLElement): boolean => {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');
    return el.matches(selector) || el.offsetParent !== null;
  };

  // Get current state
  const state = (): VimState => ({
    enabled: enabled(),
    mode: mode(),
    commandBuffer: commandBuffer(),
    lastCommand: lastCommand(),
    visualStart: null,
    currentElement: currentElement(),
    yankBuffer: yankBuffer(),
    searchBuffer: searchBuffer(),
    isSearching: searchMatches().length > 0,
    searchDirection: searchDirection(),
    repeatCount: 0,
    showHelp: showHelp(),
  });

  // Expose search function through context
  // Context value
  const value: VimContextValue = {
    state,
    executeCommand,
    setMode,
    appendToBuffer,
    clearBuffer,
    toggleHelp,
    performSearch,
    setEnabled,
  };

  return <VimContext.Provider value={value}>{props.children}</VimContext.Provider>;
};

// Hook to use vim context
export const useVim = (): VimContextValue => {
  const context = useContext(VimContext);
  if (!context) {
    throw new Error('useVim must be used within a VimProvider');
  }
  return context;
};
